import { useCallback } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { RootState, useAppSelector } from 'src/store';
import { useHomeApi } from '../api';
import {
  selectHomeCounters,
  selectHomeFilter,
  selectHomeLoading,
  selectHomeMetrics,
  setCounters,
  setError,
  setFilter as setFilterAction,
  setLoading,
  setMetrics,
} from '../slices';
import { HomeState } from '../slices/types';
import { MetricsBreakdown } from '../types';

export const useHomeService = () => {
  const dispatch = useDispatch();
  const { getState } = useStore<RootState>();
  const api = useHomeApi();

  const loading = useAppSelector(selectHomeLoading);
  const metrics = useAppSelector(selectHomeMetrics);
  const counters = useAppSelector(selectHomeCounters);
  const filter = useAppSelector(selectHomeFilter);

  const getMetrics = useCallback(
    async (companyId: string, breakdownBy: MetricsBreakdown = 'day') => {
      const { startDateInterval, endDateInterval } = selectHomeFilter(getState());
      if (!companyId || !startDateInterval || !endDateInterval) {
        return;
      }
      try {
        dispatch(setLoading({ metrics: true }));
        const response = await api.getMetrics({
          companyIds: companyId,
          startDateInterval,
          endDateInterval,
          breakdownBy,
        });
        dispatch(setMetrics(response.data));
      } catch (e) {
        dispatch(setError(e instanceof Error ? e.message : 'Failed to load metrics'));
      } finally {
        dispatch(setLoading({ metrics: false }));
      }
    },
    [api, dispatch, getState],
  );

  const getCounters = useCallback(
    async (companyId: string) => {
      if (!companyId) {
        return;
      }
      try {
        dispatch(setLoading({ counters: true }));
        const response = await api.getMetricsTotals({ companyId });
        dispatch(
          setCounters({
            contactsCount: response.data.totalContacts,
            activeCampaignsCount: response.data.activeCampaigns,
            keywordCount: response.data.totalKeywords,
            activeGroupsCount: response.data.activeGroups,
          }),
        );
      } catch (e) {
        dispatch(setError(e instanceof Error ? e.message : 'Failed to load counters'));
      } finally {
        dispatch(setLoading({ counters: false }));
      }
    },
    [api, dispatch],
  );

  const changeFilter = useCallback(
    (next: Partial<HomeState['filter']>) => {
      dispatch(setFilterAction(next));
    },
    [dispatch],
  );

  return { loading, metrics, counters, filter, getMetrics, getCounters, changeFilter };
};
